import { useState } from "react";
import {
  ActivateSubscriptionRequest,
  PAYMENT_METHOD_OPTIONS,
  Plan,
  PaymentMethodType,
} from "../models/SubscriptionModels";
import { OrganizationResponse } from "../models/OrganizationModels";
import PlanSelect from "./PlanSelect";
import "./SubscriptionForms.css";

interface ActivateSubscriptionFormProps {
  organization: OrganizationResponse | null;
  plans: Plan[];
  plansLoading: boolean;
  submitting: boolean;
  submitLabel?: string;
  submittingLabel?: string;
  onCancel?: () => void;
  onSubmit: (request: ActivateSubscriptionRequest) => void;
}

const ActivateSubscriptionForm = ({
  organization,
  plans,
  plansLoading,
  submitting,
  submitLabel = "Activate subscription",
  submittingLabel = "Activating…",
  onCancel,
  onSubmit,
}: ActivateSubscriptionFormProps) => {
  const [planId, setPlanId] = useState("");
  const [periodCount, setPeriodCount] = useState("1");
  const [amount, setAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [paymentReference, setPaymentReference] = useState("");
  const [notes, setNotes] = useState("");

  const selectedPlan = plans.find((plan) => plan.id === planId) ?? null;

  const handlePlanChange = (nextPlanId: string) => {
    setPlanId(nextPlanId);
    const plan = plans.find((p) => p.id === nextPlanId);
    if (plan) {
      const periods = Math.max(1, Number(periodCount) || 1);
      setAmount(String(plan.priceMonthly * periods));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const periods = Math.max(1, Number(periodCount) || 1);
    const parsedAmount = Number(amount);
    if (!planId || !Number.isFinite(parsedAmount) || parsedAmount <= 0) return;

    onSubmit({
      organizationId: organization?.id ?? "",
      planId,
      periodCount: periods,
      amount: parsedAmount,
      paymentMethod: Number(paymentMethod) as PaymentMethodType,
      paymentReference: paymentReference.trim() || null,
      notes: notes.trim() || null,
      paidAt: null,
    });
  };

  return (
    <form className="sub-form" onSubmit={handleSubmit}>
      <div className="sub-form__grid">
        <div className="sub-form__field">
          <label htmlFor="sub-org">Organization</label>
          <input
            id="sub-org"
            type="text"
            value={organization?.name ?? "Select an organization"}
            disabled
          />
        </div>

        <div className="sub-form__field">
          <label htmlFor="sub-plan">Plan *</label>
          <PlanSelect
            plans={plans}
            value={planId}
            loading={plansLoading}
            disabled={submitting}
            onChange={handlePlanChange}
          />
          {selectedPlan?.description && (
            <span className="sub-form__hint">{selectedPlan.description}</span>
          )}
        </div>

        <div className="sub-form__field">
          <label htmlFor="sub-periods">Period count *</label>
          <input
            id="sub-periods"
            type="number"
            min={1}
            value={periodCount}
            disabled={submitting}
            onChange={(e) => setPeriodCount(e.target.value)}
          />
          <span className="sub-form__hint">
            Number of billing periods (e.g. 1 = {selectedPlan?.billingPeriodDays ?? 30} days if plan is monthly)
          </span>
        </div>

        <div className="sub-form__field">
          <label htmlFor="sub-amount">Amount ({selectedPlan?.currency ?? "BDT"}) *</label>
          <input
            id="sub-amount"
            type="number"
            min={0}
            step="0.01"
            value={amount}
            placeholder="0"
            disabled={submitting}
            onChange={(e) => setAmount(e.target.value)}
          />
        </div>

        <div className="sub-form__field">
          <label htmlFor="sub-method">Payment method *</label>
          <select
            id="sub-method"
            value={paymentMethod}
            disabled={submitting}
            onChange={(e) => setPaymentMethod(e.target.value)}
          >
            <option value="">Select payment method…</option>
            {PAYMENT_METHOD_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="sub-form__field">
          <label htmlFor="sub-reference">Payment reference</label>
          <input
            id="sub-reference"
            type="text"
            value={paymentReference}
            placeholder="e.g. bKash-TXN-123"
            disabled={submitting}
            onChange={(e) => setPaymentReference(e.target.value)}
          />
        </div>

        <div className="sub-form__field sub-form__field--full">
          <label htmlFor="sub-notes">Notes</label>
          <textarea
            id="sub-notes"
            rows={2}
            value={notes}
            disabled={submitting}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>
      </div>

      <div className="sub-form__actions">
        {onCancel && (
          <button
            type="button"
            className="sub-form__btn sub-form__btn--secondary"
            onClick={onCancel}
            disabled={submitting}
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          className="sub-form__btn sub-form__btn--primary"
          disabled={
            submitting ||
            !planId ||
            !paymentMethod ||
            !amount ||
            Number(amount) <= 0
          }
        >
          {submitting ? submittingLabel : submitLabel}
        </button>
      </div>
    </form>
  );
};

export default ActivateSubscriptionForm;